import { FC, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { HiArrowRight } from "react-icons/hi";
import TopBadge from "./content/TopBadge";
import MainContent from "./content/MainContent";
import StatsPanel from "./content/StatsPanel";

const ContentContainer: FC = () => {
  const [workflowComplete, setWorkflowComplete] = useState(false);

  return (
    <div className="relative desktop:max-w-7xl laptop:max-w-5xl tablet:max-w-2xl mobile:max-w-sm mx-auto px-6">
      <div className="desktop:pt-32 laptop:pt-28 tablet:pt-24 mobile:pt-20">
        <TopBadge />
        <div className="grid desktop:grid-cols-12 laptop:grid-cols-12 tablet:grid-cols-1 mobile:grid-cols-1 gap-20">
          <MainContent />
          <StatsPanel onComplete={setWorkflowComplete} />
        </div>
        
        {/* Aligned bottom containers */}
        <div className="grid desktop:grid-cols-12 laptop:grid-cols-12 tablet:grid-cols-1 mobile:grid-cols-1 desktop:gap-20 laptop:gap-20 tablet:gap-8 mobile:gap-6 desktop:mt-16 laptop:mt-16 tablet:mt-12 mobile:mt-10 items-stretch">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="desktop:col-span-7 laptop:col-span-7 tablet:col-span-1 mobile:col-span-1 flex mobile:hidden"
          >
            <Link 
              to="/api" 
              className="relative w-full desktop:p-8 laptop:p-8 tablet:p-6 mobile:p-4 rounded-3xl overflow-hidden group hover:scale-[1.02] transform transition-all duration-200 bg-gradient-to-r from-gray-900 via-black to-gray-900 desktop:h-[140px] laptop:h-[140px] tablet:h-[120px] mobile:h-[110px] flex items-center will-change-transform"
            >
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(255,255,255,0.1),_transparent_50%)]"></div>
              <div className="relative flex desktop:items-center laptop:items-center tablet:items-center mobile:items-center desktop:justify-between laptop:justify-between tablet:justify-between mobile:flex-col w-full desktop:space-y-0 laptop:space-y-0 tablet:space-y-0 mobile:space-y-2">
                <div className="desktop:flex-1 laptop:flex-1 tablet:flex-1 mobile:w-full">
                  <div className="text-gray-400 desktop:text-sm laptop:text-sm tablet:text-xs mobile:text-xs font-medium desktop:mb-2 laptop:mb-2 tablet:mb-1 mobile:mb-1">DEVELOPER API</div>
                  <div className="text-white desktop:text-xl laptop:text-xl tablet:text-lg mobile:text-base font-semibold">
                    Building for your own use cases internally?
                  </div>
                </div>
                <div className="flex items-center desktop:ml-4 laptop:ml-4 tablet:ml-4 mobile:ml-0">
                  <span className="text-white desktop:text-lg laptop:text-lg tablet:text-base mobile:text-sm font-medium desktop:mr-2 laptop:mr-2 tablet:mr-1 mobile:mr-1">Check out our API</span>
                  <HiArrowRight className="desktop:w-6 laptop:w-6 tablet:w-5 mobile:w-4 desktop:h-6 laptop:h-6 tablet:h-5 mobile:h-4 text-white transform group-hover:translate-x-1 transition-transform duration-200" />
                </div>
              </div>
            </Link>
          </motion.div>
          
          {/* Always reserve space for the 80% container to prevent layout shifts */}
          <div className="desktop:col-span-5 laptop:col-span-5 tablet:col-span-1 mobile:col-span-1 flex desktop:h-[140px] laptop:h-[140px] tablet:h-[120px] mobile:h-[110px]">
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ 
                opacity: workflowComplete ? 1 : 0,
                scale: workflowComplete ? 1 : 0.8
              }}
              transition={{ 
                duration: 0.5, 
                ease: "easeOut",
                type: "spring",
                stiffness: 200,
                damping: 20
              }}
              className={`desktop:p-8 laptop:p-8 tablet:p-6 mobile:p-4 bg-gradient-to-r from-green-500 to-emerald-500 rounded-3xl text-white overflow-hidden relative w-full flex items-center justify-center will-change-transform ${
                workflowComplete ? 'shadow-2xl shadow-green-500/30 ring-4 ring-green-400/20' : ''
              }`}
            >
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(255,255,255,0.1),_transparent_50%)]"></div>
              
              {/* Animated background effect */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: workflowComplete ? 1 : 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="absolute inset-0 bg-gradient-to-r from-green-400/20 to-emerald-400/20 rounded-3xl"
              />
              
              {/* Pulsing glow effect */}
              <motion.div
                animate={{ 
                  scale: workflowComplete ? [1, 1.05, 1] : 1,
                  opacity: workflowComplete ? [0.5, 0.8, 0.5] : 0
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="absolute inset-0 bg-gradient-to-r from-green-300/30 to-emerald-300/30 rounded-3xl blur-sm"
              />
              
              <div className="relative text-center z-10">
                <motion.div 
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ 
                    scale: workflowComplete ? 1 : 0.5,
                    opacity: workflowComplete ? 1 : 0
                  }}
                  transition={{ duration: 0.4, delay: 0.3, ease: "easeOut" }}
                  className="desktop:text-5xl laptop:text-5xl tablet:text-4xl mobile:text-3xl font-bold desktop:mb-2 laptop:mb-2 tablet:mb-1 mobile:mb-1"
                >
                  80%
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ 
                    opacity: workflowComplete ? 1 : 0,
                    y: workflowComplete ? 0 : 10
                  }}
                  transition={{ duration: 0.3, delay: 0.5 }}
                  className="desktop:text-base laptop:text-base tablet:text-sm mobile:text-xs opacity-90"
                >
                  Time Saved on Workflow
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentContainer;
